import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Checkbox, FormControl, IconButton, InputAdornment, MenuItem, Popover, Select, Snackbar, Stack, TextField, TextFieldProps, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import classNames from 'classnames';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { TableContext } from '../../contexts/TableContext';
import { scrollbarStyle } from '../../shared/dashboard';
import theme from "../../theme";
import ConfirmDialog from '../modals/ConfirmDialog';
import Tag from "../molecules/dashboard-tags/Tag";
import CopyPasteContentCard from '../molecules/home-companies/CopyPasteContentCard';
import { FallbackLoading } from '../templates/loader';

const useStyles = makeStyles(() => ({
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 32,
        height: 32,
    },
    group: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        textTransform: 'capitalize',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: '#666666',
        paddingBottom: 8,
    },
    sublabel: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '400',
        color: '#aeaeae',
        paddingBottom: 8,
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            borderRadius: 8,
            padding: '0 8px !important',
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	border: `2px solid ${theme.colors.neutral['400']} !important`,
            },
            "&:hover fieldset": {
                border: `1px solid ${theme.colors.neutral['600']} !important`,
            },
            "&.Mui-focused fieldset": {
              	border: `2px solid ${theme.palette.primary.main} !important`,
            },
        },
    },
    fieldExtended: {
        minWidth: 'fit-content',
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            height: 'unset',
            padding: '8px !important',
            "& > textarea": {
                fontFamily: 'Inter',
                fontSize: '0.95rem',
                padding: 'unset',
            },
        },
    },
    formContainer: {
        minWidth: 60,
        padding: 0,
        border: 0,
        "& > .MuiInputBase-root": {
            "& > .MuiSelect-select": {
                fontSize: '1rem',
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                textTransform: 'capitalize',
                paddingTop: '0 !important',
                paddingBottom: '0 !important',
            },
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                fill: theme.colors.neutral['600'],
                "&.Mui-disabled": {
                    display: 'none',
                },
            },
            "& fieldset": {
                border: 0,
            },
        },
    },
    menu: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 400,
        color: 'gray',
    },
    closeIcon: {
        width: '20px',
        height: '20px',
        color: theme.colors.neutral['500'],
        cursor: 'pointer',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    deleteIcon: {
        cursor: 'pointer',
        color: theme.colors.neutral['500'],
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    addTags: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    addTag: {
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
        cursor: 'pointer',
        userSelect: 'none',
        paddingRight: 8,
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '20px !important',
            height: '20px !important',
        }
    },
    checkBoxText: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    copyPaste: {
        width: 140,
        padding: '0 !important',
        fontFamily: 'Lato',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: theme.colors.cyan['400'],
        textTransform: 'none',
        justifyContent: 'flex-start',
        "&:hover": {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        }
    },
    delete: {
        width: 140,
        padding: '0 !important',
        fontFamily: 'Lato',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: theme.colors.error['500'],
        textTransform: 'none',
        justifyContent: 'flex-start',
        "& > .MuiButton-startIcon": {
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                color: theme.palette.primary.main,
            }
        },
        "&:hover": {
            backgroundColor: 'transparent',
        }
    },
}))

const TagSelectBox: React.FC<{
    tag: string,
    tags : string[],
    allTags?: string[],
    onFilter: (tags : string[]) => void,
}> = memo(({ tag, tags, allTags, onFilter }) => {
    const classes = useStyles();

    if (tag === '[[ALL]]') {
        return (<>
            <Checkbox edge="end"
                className={classes.checkBox}
                checked={tags.length === allTags?.length}
                onClick={(e: any) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onFilter(tags.length === allTags?.length ? [] : allTags ?? []);
                }}
                disableRipple />
        </>);
    }

    return (<>
        <Checkbox edge="end"
            className={classes.checkBox}
            checked={tags?.some(t => t === tag)}
            onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                onFilter(!tags.some(t => t === tag) ? [...tags, tag] : tags.filter(t => t !== tag));
            }}
            disableRipple />
    </>);
});

const TagCounts = [1, 2, 3, 4, 5];

const TagGroups: React.FC<{
    group: string,
    tags: string[],
    tagsCount: number,
    context: string,
    create?: boolean,
    onContext: (context: string) => void,
    onUpdateTags: (tags: string[], tagsCount: number) => void,
    onEditTitle?: (title: string) => void,
    onDelete?: () => void,
 }> = ({ group, tags, tagsCount, context, create, onContext, onUpdateTags, onEditTitle, onDelete }) => {
    const classes = useStyles();
    const { headers } = useContext(TableContext);
    const [loading] = useState<boolean>(false);
    const [newTag, setNewTag] = useState<string>('')
    const [snackIsOpen, setSnackIsOpen] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [copyPasteDialogOpen, setCopyPasteDialogOpen] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [deletableTags, setDeletableTags] = useState<string[]>([]);

    const [edit, setEdit] = useState<boolean>(false);
    const [editValue, setEditValue] = useState<string>('');
    const inputRef = useRef<TextFieldProps>(null);
    const contextRef = useRef<TextFieldProps>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const debounced = useDebouncedCallback((value) => {
        setNewTag(value);
    }, 30, { maxWait: 100 });

    const isExisting = useMemo(() =>
        !!headers?.find(header => header.trim().toLowerCase() === editValue.trim().toLowerCase())
    , [editValue, headers]);

    const handleCreateNewTag = useCallback(() => {
        if (!newTag) {
            setErrorMessage('Please enter a tag');
            setSnackIsOpen(true);
            return;
        }

        onUpdateTags([...tags, newTag], tagsCount);
        setNewTag('');
        setErrorMessage('Successfully created new tag');
        setSnackIsOpen(true);
    }, [newTag, onUpdateTags, tags, tagsCount]);

    const handleCreateBulkTags = useCallback((newTags: string[]) => {
        onUpdateTags(Array.from(new Set([...tags, ...newTags])), tagsCount);
        setErrorMessage('Successfully created new tags');
        setSnackIsOpen(true);
    }, [onUpdateTags, tags, tagsCount]);

    const handleDeleteTags = useCallback(() =>{
        onUpdateTags(tags.filter(t => ![...deletableTags].includes(t)), tagsCount);
        setSelectedTags(prev => prev.filter(t => ![...deletableTags].includes(t)));
        setDeletableTags([]);
    }, [onUpdateTags, tags, deletableTags, tagsCount]);

    useEffect(() => {
        if (create) {
            setEdit(true);
            setEditValue(group);
        }
    // eslint-disable-next-line
    }, [create]);

    return (<>
        <Stack width="100%" height="100%" p={1}>
            <Stack direction="row" spacing={2} alignItems="flex-start" width="100%" height="100%">
                <Stack direction="column" spacing={2} alignItems="flex-start" justifyContent="stretch" width={400} height="100%">
                    <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" width="100%" pl={1}>
                        {!create && (<Typography className={classes.title}> {'Field name'} </Typography>)}
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pr="12px" width="100%" height="32px">
                            {!edit ? (<>
                                <Typography className={classes.group}>{group}</Typography>
                                <IconButton onClick={() => {
                                    setEditValue(group);
                                    setEdit(true);
                                }} disableRipple>
                                    <EditOutlinedIcon
                                        fontSize="medium"
                                        sx={{ mr: '8px', color: theme.colors.neutral['500'] }} />
                                </IconButton>
                        </>) : (<>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width={`100%`} maxWidth="100%">
                                <TextField variant="standard" type="text"
                                    placeholder={'Enter new tags name...'}
                                    inputRef={inputRef}
                                    defaultValue={group}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    style={{ width: "100%" }}
                                    inputProps={{ style: { fontSize: '1.5rem', color: '#048290', padding: 0, minHeight: "auto" }}}
                                    autoFocus />
                                    <Stack direction="row" spacing={1}>
                                        {!isExisting ? (
                                            <IconButton onClick={() => !!editValue ? (() => {
                                                    onEditTitle?.(inputRef.current?.value as string);
                                                    setEdit(false);
                                                })() : undefined} sx={{ cursor: 'pointer' }}
                                                disabled={!editValue}>
                                                <CheckIcon fontSize="small" sx={{
                                                    "&.MuiSvgIcon-root": {
                                                        fill: '#7bd4d4'
                                                    } }} />
                                            </IconButton>
                                        ) : (
                                            <Tooltip placement="top" title={
                                                <Typography component="span" fontSize="0.9rem" fontWeight="600">
                                                    {`Column already exists`}
                                                </Typography>
                                                } componentsProps={{tooltip:{sx:{
                                                    display: 'flex', alignItems: 'center', backgroundColor: 'rgba(150, 150, 150, 0.9)',
                                                    height: '24px', padding: 2,
                                                }}}}>
                                                <IconButton sx={{ cursor: 'not-allowed' }}>
                                                    <CheckIcon fontSize="small" sx={{
                                                        "&.MuiSvgIcon-root": {
                                                            fill: theme.colors.neutral['500']
                                                        } }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton onClick={() => {create ? onDelete!() : setEdit(false);}}>
                                            <ClearIcon fontSize="small" sx={{ "&.MuiSvgIcon-root": {fill: 'gray'} }} />
                                        </IconButton>
                                    </Stack>
                            </Stack>
                        </>)}
                        </Stack>
                    </Stack>
                    {!create && (
                        <Stack direction="column" spacing={2} alignItems="flex-start" justifyContent="stretch" width="100%" height="100%">
                            <Stack alignItems="flex-start" justifyContent="flex-start" width="100%" pl={1}>
                                <Typography className={classes.title}>
                                    {'Field description'}
                                </Typography>
                                <Typography className={classes.sublabel}>
                                    {'Enhance tag accuracy by providing additional'}<br/>
                                    {'context on how these should get assigned.'}
                                </Typography>
                            </Stack>
                            <TextField variant="outlined"
                                className={classNames(classes.inputField, classes.fieldExtended)}
                                defaultValue={context}
                                placeholder={'Enter additional context here...'}
                                onChange={(e) => onContext(e.target.value)}
                                inputRef={contextRef}
                                minRows={3}
                                maxRows={5}
                                sx={{ width: '100%' }}
                                multiline
                            />
                            <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%" pl={1}>
                                <Typography color="#666666">{`Number of tags per company:`}</Typography>
                                <FormControl variant="outlined" className={classes.formContainer}>
                                    <Select value={tagsCount}
                                        onChange={(e) => onUpdateTags(tags, Number(e.target.value))} displayEmpty>
                                        {TagCounts.map((option, i) => (
                                            <MenuItem className={classes.menu}
                                                value={option}
                                                key={"tags-group-269-" + i}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
                {!create && (
                    <Stack direction="column" spacing={2} alignItems="flex-start" justifyContent="stretch" width={400} height="100%" p={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                            <Typography className={classes.addTags}>
                                {'Add tags:'}
                            </Typography>
                            <Button variant="text"
                                className={classes.copyPaste}
                                ref={buttonRef}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setCopyPasteDialogOpen(true);
                                }} disableRipple>
                                {'Copy/paste from list'}
                            </Button>
                        </Stack>
                        <TextField variant="outlined"
                            className={classes.inputField}
                            placeholder={`Add a new ${group} ...`}
                            value={newTag}
                            disabled={loading}
                            onChange={(e) => debounced(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                    handleCreateNewTag();
                            }}
                            sx={{ width: '100%' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" onClick={handleCreateNewTag}>
                                        <Typography className={classes.addTag}>Add</Typography>
                                    </InputAdornment>
                                )}}
                        />
                        {loading ? (<FallbackLoading />) : (<>
                            {!!tags.length && (
                                <Stack spacing={1} width="100%" height="290px">
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%" py="4px">
                                            <TagSelectBox tag="[[ALL]]" tags={selectedTags} allTags={tags}
                                                onFilter={(tags) => {setSelectedTags(tags); setDeletableTags(tags);}} />
                                            <Typography className={classes.checkBoxText}> {'Select All'} </Typography>
                                        </Stack>
                                        {!!selectedTags.length && (
                                            <Button variant="text"
                                                className={classes.delete}
                                                startIcon={<DeleteOutlineIcon
                                                    sx={{ color: `${theme.colors.error['500']} !important` }} />}
                                                onClick={(e: any) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    setDeleteDialogOpen(true);
                                                }} disableRipple> {'Delete all'} </Button>
                                        )}
                                    </Stack>
                                    <Stack spacing={2} width="100%" height="calc(100vh - 470px)" pt={1}
                                        sx={{ overflowY: 'auto', ...scrollbarStyle }}>
                                        {tags.map((tag, i) => (
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                                                key={'tag-groups-150-' + i}>
                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%">
                                                    <TagSelectBox tag={tag} tags={selectedTags}
                                                        onFilter={(tags) => {setSelectedTags(tags); setDeletableTags(tags);}} />
                                                    <Tag title={tag} color={theme.colors.neutral['100']} />
                                                </Stack>
                                                <DeleteIcon className={classes.deleteIcon} onClick={() => {
                                                    setDeletableTags([tag]);
                                                    setDeleteDialogOpen(true);
                                                }} />
                                            </Stack>
                                        ))}
                                        <Box height={32}>&nbsp;</Box>
                                    </Stack>
                                </Stack>
                            )}
                        </>)}
                    </Stack>
                )}
            </Stack>
        </Stack>
        {snackIsOpen && (
            <Snackbar
                open={snackIsOpen}
                autoHideDuration={2000}
                onClose={() => setSnackIsOpen(false)}
                message={errorMessage}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            />
        )}
        {copyPasteDialogOpen && (
            <Popover open={copyPasteDialogOpen} anchorEl={buttonRef.current}>
                <Box width="fit-content" height="fit-content">
                    <CopyPasteContentCard
                        onSave={handleCreateBulkTags}
                        onClose={() => setCopyPasteDialogOpen(false)} />
                </Box>
            </Popover>
        )}
        {deleteDialogOpen && (
            <ConfirmDialog
                title="Delete confirmation"
                content={`Are you sure you want to delete ${selectedTags.length > 1 ? 'these tags' : 'this tag'}?`}
                open={deleteDialogOpen}
                confirmCallback={() => {
                    handleDeleteTags();
                    setErrorMessage('Tag successfully deleted!');
                    setSnackIsOpen(true);
                    setDeleteDialogOpen(false);
                }}
                cancelCallback={() => setDeleteDialogOpen(false)}
            />
        )}
    </>);
}

export default TagGroups;